import React, { useEffect, useMemo, useState } from "react";
import { api, type AgentOsDraftEntityType, type AgentOsDraftStatus, type AgentOsDraftView } from "../api";
import { ActionButton, DiffView, FormField, StateMessage, StatusBadge, type DiffLine } from "../design-system";
import type { AgentOsStudioRecord } from "./agentOsStudioModel";

export interface AgentOsDraftFlowProps {
  entityType: AgentOsDraftEntityType;
  entityId: string | null;
  baseHash: string | null;
  initialPayload: Record<string, unknown>;
  enabled: boolean;
  editorMode?: "json" | "guided";
  onRuntimeReadback?: (input: { action: "apply" | "rollback"; record: AgentOsStudioRecord | null }) => void;
  onDraftStatus?: (status: AgentOsDraftStatus | null) => void;
}

const ACTION_LABELS: Partial<Record<AgentOsDraftStatus, string>> = {
  draft: "Validieren",
  approved: "Anwenden",
  applied: "Rollback",
};

export function AgentOsDraftFlow({ entityType, entityId, baseHash, initialPayload, enabled, editorMode = "json", onRuntimeReadback, onDraftStatus }: AgentOsDraftFlowProps): JSX.Element {
  const initialPayloadJson = JSON.stringify(initialPayload, null, 2);
  const [payloadText, setPayloadText] = useState(() => initialPayloadJson);
  const [draft, setDraft] = useState<AgentOsDraftView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [auditReadback, setAuditReadback] = useState<string | null>(null);
  const [runtimeReadback, setRuntimeReadback] = useState<string | null>(null);
  useEffect(() => {
    setPayloadText(initialPayloadJson);
    setDraft(null);
    setError(null);
    setBusy(false);
    setAuditReadback(null);
    setRuntimeReadback(null);
  }, [entityType, entityId, baseHash, initialPayloadJson]);
  const parsedPayload = useMemo(() => {
    try {
      const value = JSON.parse(payloadText) as unknown;
      return value && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : null;
    } catch {
      return null;
    }
  }, [payloadText]);

  async function run(action: "create" | "validate" | "simulate" | "approve" | "apply" | "rollback" | "delete"): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      if (action === "create") {
        const generatedBaseAllowed = (["agent", "routine", "loop"].includes(entityType) && (parsedPayload?.operation === "create" || parsedPayload?.operation === "clone")) || (entityType === "brain" && parsedPayload?.operation === "create");
        if (!entityId || (!baseHash && !generatedBaseAllowed) || !parsedPayload) throw new Error("Entity, Base-Hash und Entwurf sind erforderlich.");
        const result = await api.agentOsCreateDraft({ entityType, entityId, ...(baseHash ? { baseHash } : {}), payload: parsedPayload });
        setDraft(result.draft);
        onDraftStatus?.(result.draft.status);
      } else if (action === "delete" && draft) {
        await api.agentOsDeleteDraft(draft.id);
        setDraft(null);
        setAuditReadback(null);
        setRuntimeReadback("Draft gelöscht: unveröffentlichter Entwurf entfernt; keine Runtime-Schreibaktion.");
        onDraftStatus?.(null);
      } else if (draft) {
        const result = action === "apply" ? await api.agentOsApplyDraft(draft.id) : await api.agentOsDraftAction(draft.id, action as "validate" | "simulate" | "approve" | "rollback");
        setDraft(result.draft);
        onDraftStatus?.(result.draft.status);
        if ((action === "apply" || action === "rollback") && entityId) {
          try {
            const studio = await api.agentOsStudio();
            const section = entityType === "agent" ? studio.agents : entityType === "skill" ? studio.skills : entityType === "tool" ? studio.tools : entityType === "routine" ? studio.routines : entityType === "loop" ? studio.loops : entityType === "policy" ? studio.policies : entityType === "brain" ? studio.brainEntries ?? [] : [];
            const record = section.find((candidate) => candidate.id === entityId);
            if (!record) setRuntimeReadback(action === "rollback" ? "Runtime-Readback: Abwesenheit bestätigt" : "Runtime-Readback: Objekt nicht gefunden");
            else setRuntimeReadback(`Runtime-Readback: ${record.provenance.joinStatus} · ${record.version.drift}`);
            onRuntimeReadback?.({ action, record: record ?? null });
          } catch {
            setRuntimeReadback("Runtime-Readback: derzeit nicht verfügbar");
          }
        }
      }
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Mutation fehlgeschlagen.");
    } finally {
      setBusy(false);
    }
  }

  const next = draft ? ACTION_LABELS[draft.status] : "Draft anlegen";
  const simulated = useMemo(() => {
    if (!draft?.diffJson) return false;
    try { return (JSON.parse(draft.diffJson) as { simulation?: { status?: string } }).simulation?.status === "pass"; } catch { return false; }
  }, [draft?.diffJson]);
  const diffLines = useMemo<DiffLine[]>(() => {
    if (!draft?.diffJson) return [];
    let text = draft.diffJson;
    try { text = JSON.stringify(JSON.parse(draft.diffJson), null, 2); } catch { /* value remains a safe text readback */ }
    return text.split(/\r?\n/).map((content, index) => ({ number: index + 1, kind: "context", content }));
  }, [draft?.diffJson]);
  return (
    <section className="rounded-lg border border-jarvis-accent/20 bg-jarvis-accent/[0.04] p-3" data-agent-os-draft-flow aria-busy={busy || undefined}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-[10px] uppercase tracking-[0.14em] text-jarvis-accent">Unified Change Lane</p>
          <p className="mt-1 text-xs text-jarvis-muted">Draft → Validate → Simulate → Diff → Approve → Apply → Audit → Rollback</p>
        </div>
        <StatusBadge status={draft?.status ?? "idle"} label={draft?.status ?? "kein Draft"} />
      </div>
      {enabled && editorMode === "json" && (
        <div className="mt-3"><FormField id={`agent-os-draft-${entityType}`} label="Agent OS Draft JSON" help="Strukturierter Entwurf; Apply bleibt bis Validate und Owner-Freigabe gesperrt." error={parsedPayload ? null : "Der Draft muss ein gültiges JSON-Objekt sein."} required><textarea value={payloadText} onChange={(event) => setPayloadText(event.target.value)} className="min-h-28 font-mono text-xs" /></FormField></div>
      )}
      {enabled && editorMode === "guided" && <p className="mt-3 rounded-md border border-white/10 bg-black/15 px-2.5 py-2 text-[11px] text-jarvis-muted">Geführter Entwurf gebunden. Die technischen JSON-Werte bleiben im Builder ausgeblendet; Diff und Audit werden nach Validate sichtbar.</p>}
      {draft && diffLines.length > 0 && <div className="mt-3"><DiffView file={`${entityType}/${entityId ?? "draft"}`} lines={diffLines} /></div>}
      <div className="mt-2 flex flex-wrap gap-2">
        {!draft && <ActionButton variant="primary" disabled={!enabled || busy || !entityId || (!baseHash && !((["agent", "routine", "loop"].includes(entityType) && (parsedPayload?.operation === "create" || parsedPayload?.operation === "clone")) || (entityType === "brain" && parsedPayload?.operation === "create"))) || !parsedPayload} onClick={() => void run("create")}>Draft anlegen</ActionButton>}
        {!draft && ["agent", "skill", "routine", "policy", "brain"].includes(entityType) && <ActionButton variant="danger" disabled={!enabled || busy} onClick={() => setPayloadText(JSON.stringify({ operation: "deprecate", reason: "Owner deprecates this object" }, null, 2))}>Deprecation vorbereiten</ActionButton>}
        {!draft && entityType === "brain" && <ActionButton variant="danger" disabled={!enabled || busy || !entityId} onClick={() => setPayloadText(JSON.stringify({ operation: "purge", confirmPurge: false, stepUpPhrase: `PURGE ${entityId ?? ""}` }, null, 2))}>Purge-Impact vorbereiten</ActionButton>}
        {draft?.status === "draft" && <ActionButton disabled={busy} onClick={() => void run("validate")}>{next}</ActionButton>}
        {draft && ["draft", "validated", "approved", "rejected"].includes(draft.status) && <ActionButton variant="danger" disabled={busy} onClick={() => void run("delete")}>Draft löschen</ActionButton>}
        {draft?.status === "validated" && !simulated && <ActionButton disabled={busy} onClick={() => void run("simulate")}>Simulieren</ActionButton>}
        {draft?.status === "validated" && simulated && <ActionButton variant="warning" disabled={busy} onClick={() => void run("approve")}>Owner-Freigabe</ActionButton>}
        {draft?.status === "approved" && <ActionButton variant="success" disabled={busy} onClick={() => void run("apply")}>{next}</ActionButton>}
        {draft?.status === "applied" && <ActionButton variant="danger" disabled={busy} onClick={() => void run("rollback")}>{next}</ActionButton>}
        {draft?.status === "applied" && draft.auditId && <ActionButton disabled={busy} onClick={() => { setBusy(true); void api.agentOsDraftAudit(draft.id).then((result) => setAuditReadback(`${result.audit.result} · Kette ${result.audit.chainVerified ? "verifiziert" : "gebrochen"}`)).catch((cause: unknown) => setError(cause instanceof Error ? cause.message : "Audit nicht verfügbar.")).finally(() => setBusy(false)); }}>Audit lesen</ActionButton>}
      </div>
      {!enabled && <p className="mt-2 text-[11px] text-jarvis-muted">Builder-/Expert-Modus und ein kanonischer Base-Hash sind für Änderungen erforderlich.</p>}
      {error && <div className="mt-2"><StateMessage kind="error" label="Mutation fehlgeschlagen">{error}</StateMessage></div>}
      {draft?.commitRef && <p className="mt-2 text-[10px] text-jarvis-muted">Commit-Ref: {draft.commitRef} · Sync: {draft.runtimeSyncStatus}</p>}
      {auditReadback && <p className="mt-2 text-[10px] text-jarvis-accent">Audit: {auditReadback}</p>}
      {runtimeReadback && <p className="mt-2 text-[10px] text-jarvis-accent" aria-live="polite">{runtimeReadback}</p>}
    </section>
  );
}
